import styled, { keyframes } from "styled-components";
import { BRAND_C as C, COLORS } from "../../common/colors";

// Category badge colour map — keys match `cat` field in certificates.json
export const CATEGORY_COLORS = {
  One: "#fbca57",
  Two: "#6bcf63",
  Three: "#57c8fb",
  Nutrition: "#ff7f50",
  Wellness: "#9c7cf0",
};

// ─── Keyframes ────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE SHELL
// ═══════════════════════════════════════════════════════════════════════════════

export const Page = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${COLORS.pageBg};
  overflow-x: hidden;
`;
Page.displayName = "Page";

export const BgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;

  &.b1 {
    width: 520px;
    height: 520px;
    background: ${COLORS.primaryTint};
    top: -160px;
    left: -160px;
  }
  &.b2 {
    width: 380px;
    height: 380px;
    background: rgba(245, 197, 24, 0.07);
    top: 35%;
    right: -120px;
  }
  &.b3 {
    width: 280px;
    height: 280px;
    background: ${COLORS.primaryTint};
    bottom: 120px;
    left: 32%;
  }

  @media (max-width: 48em) {
    &.b1 {
      width: 260px;
      height: 260px;
    }
    &.b2 {
      width: 200px;
      height: 200px;
    }
    &.b3 {
      display: none;
    }
  }
`;
BgBlob.displayName = "BgBlob";

export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: radial-gradient(
    circle,
    rgba(61, 107, 94, 0.06) 1px,
    transparent 1px
  );
  background-size: 28px 28px;
  mask-image: linear-gradient(
    to bottom,
    transparent,
    rgba(0, 0, 0, 0.4) 15%,
    rgba(0, 0, 0, 0.4) 85%,
    transparent
  );
`;
DotGrid.displayName = "DotGrid";

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px;

  @media (min-width: 48em) {
    padding: 0 32px;
  }
  @media (min-width: 64em) {
    padding: 0 48px;
  }
`;
Inner.displayName = "Inner";

// ═══════════════════════════════════════════════════════════════════════════════
// HERO HEADER
// ═══════════════════════════════════════════════════════════════════════════════

export const HeroWrap = styled.header`
  position: relative;
  z-index: 1;
  text-align: center;
  padding-top: 38px;
  padding-right: 24px;
  padding-bottom: 52px;
  padding-left: 24px;
  animation: ${kFadeUp} 0.65s ease both;

  @media (max-width: 600px) {
    padding: 56px 20px 36px;
  }
`;
HeroWrap.displayName = "HeroWrap";

export const HeroEyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.brand};
  margin-bottom: 12px;
`;
HeroEyebrow.displayName = "HeroEyebrow";

export const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5.5vw, 3.2rem);
  font-weight: 900;
  color: ${C.text};
  letter-spacing: -0.025em;
  line-height: 1.12;
  margin-bottom: 16px;
`;
HeroTitle.displayName = "HeroTitle";

export const HeroDivider = styled.div`
  width: 52px;
  height: 3px;
  background: linear-gradient(90deg, ${C.brand}, ${C.accent});
  border-radius: 2px;
  margin: 0 auto 18px;
`;
HeroDivider.displayName = "HeroDivider";

export const HeroDesc = styled.p`
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: ${C.textMid};
  line-height: 1.78;
  max-width: 520px;
  margin: 0 auto;
`;
HeroDesc.displayName = "HeroDesc";

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY FILTER BAR
// ═══════════════════════════════════════════════════════════════════════════════

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 40px;
  padding: 0 4px;
  animation: ${kFadeUp} 0.5s ease both 0.1s;
`;
FilterBar.displayName = "FilterBar";

export const FilterBtn = styled.button`
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: pointer;
  min-height: 38px;
  white-space: nowrap;
  transition:
    background 0.2s,
    border-color 0.2s,
    color 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
  border: 1.5px solid ${({ $active }) => ($active ? C.brand : C.border)};
  background: ${({ $active }) =>
    $active
      ? `linear-gradient(135deg, ${C.brandDark}, ${C.brand})`
      : C.surface};
  color: ${({ $active }) => ($active ? "#fff" : C.textMid)};
  box-shadow: ${({ $active }) =>
    $active ? "0 3px 14px rgba(61,107,94,0.32)" : "0 1px 4px rgba(0,0,0,0.05)"};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: ${C.brand};
    box-shadow: 0 5px 18px rgba(61, 107, 94, 0.24);
  }
  &:active {
    transform: translateY(0) scale(0.97);
  }
  &:focus-visible {
    outline: 2px solid ${C.brand};
    outline-offset: 2px;
  }
`;
FilterBtn.displayName = "FilterBtn";

// ═══════════════════════════════════════════════════════════════════════════════
// GALLERY GRID
// ═══════════════════════════════════════════════════════════════════════════════

export const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;
GalleryGrid.displayName = "GalleryGrid";

// ─── Individual gallery card ──────────────────────────────────────────────────

export const GalleryCard = styled.article`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: ${C.surface};
  border: 1px solid ${C.border};
  box-shadow: 0 2px 12px rgba(61, 107, 94, 0.07);
  cursor: pointer;
  aspect-ratio: 4 / 3;

  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(61, 107, 94, 0.18);
    border-color: ${COLORS.borderGreen20};
  }
  &:focus-visible {
    outline: 2px solid ${C.brand};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.45s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 60, 480)}ms;
  }
`;
GalleryCard.displayName = "GalleryCard";

export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;

  ${GalleryCard}:hover & {
    transform: scale(1.06);
  }
`;
CardImage.displayName = "CardImage";

// ─── Category badge overlay ───────────────────────────────────────────────────

export const CategoryBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: ${({ $color }) => $color || C.accent};
  color: ${C.text};
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  pointer-events: none;
  transition: transform 0.2s ease;

  ${GalleryCard}:hover & {
    transform: scale(1.06);
  }

  @media (max-width: 700px) {
    font-size: 0.6rem;
    padding: 3px 8px;
  }
`;
CategoryBadge.displayName = "CategoryBadge";

// ═══════════════════════════════════════════════════════════════════════════════
// LIGHTBOX MODAL
// ═══════════════════════════════════════════════════════════════════════════════

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 4, 32, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${kFadeIn} 0.2s ease;
`;
ModalOverlay.displayName = "ModalOverlay";

export const ModalImage = styled.img`
  max-width: 88vw;
  max-height: 88vh;
  border-radius: 14px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
  object-fit: contain;
  display: block;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kScaleIn} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
`;
ModalImage.displayName = "ModalImage";

export const ModalClose = styled.button`
  position: fixed;
  top: 24px;
  right: 28px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: 1.5px solid rgba(255, 255, 255, 0.24);
  color: #fff;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.2s,
    transform 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
    transform: scale(1.1) rotate(90deg);
  }
  &:focus-visible {
    outline: 2px solid ${C.brand};
    outline-offset: 2px;
  }
`;
ModalClose.displayName = "ModalClose";

// ═══════════════════════════════════════════════════════════════════════════════
// EMPTY STATE
// ═══════════════════════════════════════════════════════════════════════════════

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: ${C.textSoft};

  .icon {
    font-size: 3rem;
    display: block;
    margin-bottom: 16px;
  }
  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${C.text};
    margin-bottom: 8px;
  }
  p {
    font-size: 0.88rem;
    line-height: 1.6;
  }
`;
EmptyState.displayName = "EmptyState";
